import type { ConsentPurpose, ConsentSource, LegalBasis } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from './api.ts';

// Mirrors apps/api/src/privacy/privacy.service.ts.

export interface PrivacyNotice {
  id: string;
  version: number;
  kind: 'text' | 'url';
  body: string | null;
  url: string | null;
  arcoChannels: string;
  publishedAt: string;
  publishedBy: string;
}

export interface Consent {
  id: string;
  purpose: ConsentPurpose;
  legalBasis: LegalBasis;
  source: ConsentSource;
  evidence: string | null;
  noticeVersion: number | null;
  grantedAt: string;
  recordedBy: string | null;
  revocation: {
    at: string;
    source: ConsentSource | null;
    note: string | null;
    by: string | null;
    dueOn: string;
    completedAt: string | null;
  } | null;
}

export interface PendingRevocation {
  consentId: string;
  contact: { id: string; name: string };
  purpose: ConsentPurpose;
  revokedAt: string;
  dueOn: string;
  overdue: boolean;
}

export interface Holidays {
  national: { date: string; name: string }[];
  own: { id: string; date: string; name: string }[];
}

export const privacyKeys = {
  notice: ['privacy', 'notice'] as const,
  history: ['privacy', 'notices'] as const,
  revocations: ['privacy', 'revocations'] as const,
  consents: (contactId: string) => ['privacy', 'consents', contactId] as const,
  holidays: (year: number) => ['holidays', year] as const,
};

export const useCurrentNotice = () =>
  useQuery({
    queryKey: privacyKeys.notice,
    queryFn: async () => (await api<{ notice: PrivacyNotice | null }>('/privacy/notice')).notice,
  });

export const useNoticeHistory = () =>
  useQuery({
    queryKey: privacyKeys.history,
    queryFn: () => api<PrivacyNotice[]>('/privacy/notices'),
  });

export const usePendingRevocations = () =>
  useQuery({
    queryKey: privacyKeys.revocations,
    queryFn: () => api<PendingRevocation[]>('/privacy/revocations'),
  });

export const useConsents = (contactId: string) =>
  useQuery({
    queryKey: privacyKeys.consents(contactId),
    queryFn: () => api<Consent[]>(`/contacts/${contactId}/consents`),
  });

export const useHolidays = (year: number) =>
  useQuery({
    queryKey: privacyKeys.holidays(year),
    queryFn: () => api<Holidays>(`/settings/holidays?year=${String(year)}`),
  });

/** "2026-11-06" → "06/11/2026" (a calendar date, no time zone involved). */
export function formatDay(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d ?? ''}/${m ?? ''}/${y ?? ''}`;
}
